import React, { Key, useEffect, useMemo, useState } from "react";
import { BasicDataNode, DataNode, EventDataNode } from "rc-tree/lib/interface";

import { StyledTree } from "./styledComponent";

interface CustomDataNode extends BasicDataNode {
  url?: string;
}

interface DocumentTreeProps {
  categoryName: string;
  getTreeData: () => DataNode[];
  setSelectedDocumentIds?: (ids: string[], checked?: boolean) => void;
}

// Extract all expandable keys from tree data (category and folder nodes)
const getAllExpandableKeys = (nodes: DataNode[]): Key[] => {
  const keys: Key[] = [];
  nodes.forEach((node) => {
    if (node.children && node.children.length > 0) {
      keys.push(node.key);
      keys.push(...getAllExpandableKeys(node.children));
    }
  });
  return keys;
};

const DocumentTree = ({
  categoryName,
  getTreeData,
  setSelectedDocumentIds,
}: DocumentTreeProps) => {
  const treeData = useMemo(() => getTreeData(), [getTreeData]);

  const defaultExpandedKeys = useMemo(
    () => getAllExpandableKeys(treeData),
    [treeData]
  );

  const [expandedKeys, setExpandedKeys] =
    useState<React.Key[]>(defaultExpandedKeys);

  // Update expanded keys when tree data changes
  useEffect(() => {
    setExpandedKeys(defaultExpandedKeys);
  }, [defaultExpandedKeys]);

  const onSelectTreeNode = (
    _: Key[],
    info: {
      event: "select";
      selected: boolean;
      node: EventDataNode<CustomDataNode>;
      selectedNodes: CustomDataNode[];
      nativeEvent: MouseEvent;
    }
  ) => {
    const { key } = info.node;

    const newExpandedKeys = new Set(expandedKeys);
    if (newExpandedKeys.has(key)) newExpandedKeys.delete(key);
    else newExpandedKeys.add(key);

    setExpandedKeys(Array.from(newExpandedKeys));
  };

  const onExpand = (expandedKeys: Key[]) => setExpandedKeys(expandedKeys);

  const getIdKey = (key: string) => /\d/.test(key);

  return (
    <StyledTree
      checkable={!!setSelectedDocumentIds}
      onCheck={(values, e: any) => {
        if (!setSelectedDocumentIds) {
          return;
        }

        if (!e.checked) {
          // get unchecked ids from the subtree or from one field
          const uncheckedNodes = e.node.children
            ? e.node.children.map((node: any) => node.key).filter(getIdKey)
            : [e.node.key];
          setSelectedDocumentIds(uncheckedNodes, false);
        } else {
          setSelectedDocumentIds(
            e.checkedNodes.map((node: any) => node.key).filter(getIdKey)
          );
        }
      }}
      key={categoryName}
      data-test="documentUploadTree"
      showLine
      expandedKeys={expandedKeys}
      treeData={treeData}
      onSelect={onSelectTreeNode}
      onExpand={onExpand}
    />
  );
};

export default DocumentTree;
